// ---------------------------------------------------------------------------
// YouAreYou (uru) — Settings Page
//
// Protected page rendered at /settings. Accessed via the user profile button
// (top-right "My Profile") or the mobile bottom tab bar "Settings" tab.
//
// Matches the desktop mockup:
//   screen-mockups/desktop/user-account-identity-provacy-subscription.png
//
// Layout:
//   Desktop: Sidebar navigation (left) + content area (right)
//   Mobile: Horizontal tab bar (top) + content area
//
// Three sections (each renders as a separate screen when selected):
//   1. Identity — Identity Factors grid (7 verification cards with status badges)
//   2. Privacy & Security — Passkey Management, Data Sharing, ZK Backups
//   3. Account — Profile details + Delete Account danger zone
//
// Data flow:
//   - GET /api/user/me → user profile (name, email, phone)
//   - GET /api/account/passkeys → passkey count for Privacy section
//   - KYC status data would come from GET /api/verify/requests/check-status/{uuid}
//     but we don't have the UUID in the user profile. Identity factors are
//     presentational for now, matching the mockup layout.
//
// Known gaps:
//   - Identity factor data (which verifications are completed) is not available
//     via a single API endpoint. The KYC status check requires a UUID from the
//     verification request. For now, factors are presentational/placeholder.
//   - Data Sharing Preferences and ZK Backups have no documented API endpoints.
//   - "Edit Details" action is placeholder.
//   - Delete Account has no documented API endpoint.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  listPasskeys,
  getUruMe,
  updateProfile,
  uploadAvatar,
  deletePasskey,
  updatePasskey,
  passkeyRegisterBegin,
  passkeyRegisterComplete,
  createSelfKyc,
  getSelfKycStatus,
  saveGlobalLinkConfig,
} from "@/api/endpoints";
import {
  startRegistration,
  browserSupportsWebAuthn,
  type RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import type { UruKyc, UruMeUser, PasskeyInfo, SelfKycCreateRequest } from "@/types/api";
import { resolveAvatarUrl } from "@/types/api";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import { cn, formatPhoneDisplay } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Section IDs
// ---------------------------------------------------------------------------

type SectionId = "identity" | "privacy" | "account";

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: "identity", label: "Identity", icon: "verified_user" },
  { id: "privacy", label: "Privacy & Security", icon: "shield_lock" },
  { id: "account", label: "Account", icon: "person" },
];

// ---------------------------------------------------------------------------
// Identity Factor definitions (presentational — from mockup)
// ---------------------------------------------------------------------------

type FactorStatus = "verified" | "pending" | "not_linked";

interface IdentityFactor {
  icon: string;
  label: string;
  detail: string;
  status: FactorStatus;
  /** Background color class for the icon circle based on status. */
  iconBg: string;
  iconColor: string;
  /** If true, this card spans full width (md:col-span-2). */
  fullWidth?: boolean;
  /** API config key (e.g. "gov_id") — present for factors that can be added via a verification link. */
  configKey?: string;
}

function getFactorBadge(status: FactorStatus): {
  label: string;
  className: string;
} {
  switch (status) {
    case "verified":
      return {
        label: "Verified",
        className: "bg-green-100 text-green-700",
      };
    case "pending":
      return {
        label: "Pending",
        className: "bg-orange-100 text-orange-700",
      };
    case "not_linked":
      return {
        label: "Not Linked",
        className: "bg-gray-100 text-text-muted",
      };
  }
}

/**
 * Build the identity factors list from real KYC data from GET /api/uru/me.
 * kyc is null while loading or if the fetch fails — cards show "Not linked" in that case.
 */
function buildIdentityFactors(
  phone: string | undefined,
  email: string | undefined,
  kyc: UruKyc | null,
  isPhoneVerified: boolean,
): IdentityFactor[] {
  const maskedPhone = phone
    ? formatPhoneDisplay(phone).replace(/\d(?=\d{2})/g, "•")
    : "Not provided";

  const verifiedStyle = {
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
  } as const;
  const unverifiedStyle = {
    iconBg: "bg-gray-100",
    iconColor: "text-text-muted",
  } as const;
  const pendingStyle = {
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
  } as const;

  return [
    {
      icon: "badge",
      label: "Government ID",
      detail: kyc?.is_gov_id_verified ? "Verified" : "Not verified",
      status: kyc?.is_gov_id_verified ? "verified" : "not_linked",
      ...(kyc?.is_gov_id_verified ? verifiedStyle : unverifiedStyle),
      configKey: "gov_id",
    },
    {
      icon: "face",
      label: "Liveness Scan",
      detail: kyc?.is_liveness_verified ? "Face scan verified" : "Not verified",
      status: kyc?.is_liveness_verified ? "verified" : "not_linked",
      ...(kyc?.is_liveness_verified ? verifiedStyle : unverifiedStyle),
      configKey: "liveness",
    },
    {
      icon: "smartphone",
      label: "Phone Verification",
      detail: maskedPhone,
      status: isPhoneVerified ? "verified" : ("pending" as FactorStatus),
      ...(isPhoneVerified ? verifiedStyle : pendingStyle),
      // Phone is verified via the registration flow — no configKey
    },
    {
      icon: "mail",
      label: "Email Address",
      detail: email ?? "Not provided",
      status: kyc?.is_email_verified ? "verified" : ("not_linked" as FactorStatus),
      ...(kyc?.is_email_verified ? verifiedStyle : unverifiedStyle),
      configKey: "email",
    },
    {
      icon: "lock",
      label: "SSN / Tax ID",
      detail: kyc?.is_ssn_verified ? "Verified" : "Not linked",
      status: kyc?.is_ssn_verified
        ? "verified"
        : ("not_linked" as FactorStatus),
      ...(kyc?.is_ssn_verified ? verifiedStyle : unverifiedStyle),
      configKey: "ssn",
    },
    {
      icon: "location_on",
      label: "Home Address",
      detail: kyc?.is_address_verified ? "Address verified" : "Not linked",
      status: kyc?.is_address_verified
        ? "verified"
        : ("not_linked" as FactorStatus),
      ...(kyc?.is_address_verified ? verifiedStyle : unverifiedStyle),
      configKey: "address",
      fullWidth: true,
    },
  ];
}

// ---------------------------------------------------------------------------
// Trust Factors Section
// ---------------------------------------------------------------------------

interface IdentitySectionProps {
  factors: IdentityFactor[];
  /** Called with fresh data after a successful self-service KYC completes. */
  onKycUpdated: (data: { kyc: UruKyc; user: UruMeUser }) => void;
  /** Pre-fill values from the user's profile for data-input checks. */
  prefill?: { email?: string; firstName?: string; lastName?: string };
}

function AskToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <div className="flex rounded-lg bg-gray-100 p-0.5" role="radiogroup">
      <button
        type="button"
        role="radio"
        aria-checked={enabled}
        onClick={() => onToggle(true)}
        className={cn(
          "rounded-md px-3 py-1.5 text-xs font-bold transition-all",
          enabled
            ? "bg-white text-text-primary shadow-sm"
            : "text-text-muted hover:text-text-secondary",
        )}
      >
        Ask
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={!enabled}
        onClick={() => onToggle(false)}
        className={cn(
          "rounded-md px-3 py-1.5 text-xs font-bold transition-all",
          !enabled
            ? "bg-white text-text-primary shadow-sm"
            : "text-text-muted hover:text-text-secondary",
        )}
      >
        Skip
      </button>
    </div>
  );
}

// Data-input factors need form fields; interactive factors just need a redirect.
const INTERACTIVE_KEYS = new Set(["gov_id", "liveness"]);
const DATA_INPUT_KEYS = new Set(["email", "ssn", "address"]);

type KycPhase = "selecting" | "collecting" | "submitting" | "success" | "existing";

function IdentitySection({ factors, onKycUpdated, prefill }: IdentitySectionProps) {
  const addableFactor = (f: IdentityFactor) =>
    f.status !== "verified" && !!f.configKey;

  const hasAddable = factors.some(addableFactor);

  const [isAdding, setIsAdding] = useState(false);
  const [phase, setPhase] = useState<KycPhase>("selecting");
  // Default all to false — user explicitly selects what they want to add
  const [toggles, setToggles] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      factors.filter(addableFactor).map((f) => [f.configKey!, false]),
    ),
  );

  // Form fields for data-input factors — pre-populated from user profile where possible
  const [formData, setFormData] = useState({
    email: prefill?.email ?? "",
    ssn: "",
    firstName: prefill?.firstName ?? "",
    lastName: prefill?.lastName ?? "",
    dob: "",
    addressLine1: "",
    addressCity: "",
    addressState: "",
    addressPostal: "",
    consented: false,
  });
  const [kycError, setKycError] = useState<string | null>(null);
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function handleToggle(configKey: string, value: boolean) {
    setToggles((prev) => ({ ...prev, [configKey]: value }));
  }

  const selectedKeys = Object.entries(toggles)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const needsDataInput = selectedKeys.some((k) => DATA_INPUT_KEYS.has(k));
  const hasInteractive = selectedKeys.some((k) => INTERACTIVE_KEYS.has(k));

  // Poll for KYC completion after returning from interactive verification
  useEffect(() => {
    if (!pendingUuid) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        const result = await getSelfKycStatus(pendingUuid);
        if (result.pvr_status === "completed" || result.pvr_status === "failed") {
          clearInterval(pollTimerRef.current!);
          if (result.pvr_status === "completed") {
            // Refresh the profile
            try {
              const uruMe = await getUruMe();
              onKycUpdated({ kyc: uruMe.kyc, user: uruMe.user });
            } catch { /* non-critical */ }
            setPhase("success");
          } else {
            setKycError("Verification failed. Please try again.");
            setPhase("selecting");
          }
          setPendingUuid(null);
        }
      } catch { /* ignore poll errors */ }
    }, 4000);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [pendingUuid, onKycUpdated]);

  async function handleSubmit() {
    setKycError(null);
    setPhase("submitting");

    const req: SelfKycCreateRequest = {};

    // Add require_* flags for selected factors
    for (const key of selectedKeys) {
      if (key === "gov_id") req.require_gov_id = true;
      else if (key === "liveness") req.require_liveness = true;
      else if (key === "email") req.require_email = true;
      else if (key === "ssn") req.require_ssn = true;
      else if (key === "address") req.require_address = true;
    }

    // Add inline data for data-input factors
    if (toggles["email"]) req.email = formData.email;
    if (toggles["ssn"]) {
      req.ssn = formData.ssn.replace(/\D/g, "");
      req.first_name = formData.firstName;
      req.last_name = formData.lastName;
      req.date_of_birth = formData.dob;
    }
    if (toggles["address"]) {
      req.address_line1 = formData.addressLine1;
      req.address_city = formData.addressCity;
      req.address_state = formData.addressState;
      req.address_postal_code = formData.addressPostal;
    }
    if (needsDataInput) req.consented_to_terms = true;

    try {
      const result = await createSelfKyc(req);

      if (result.existing) {
        setResumeUrl(result.verification_url ?? null);
        setPendingUuid(result.uuid);
        setPhase("existing");
        return;
      }

      if (result.verification_url) {
        // Store uuid for polling on return
        setPendingUuid(result.uuid);
        window.location.href = result.verification_url;
        return;
      }

      // Data-input only — refresh and show success
      try {
        const uruMe = await getUruMe();
        onKycUpdated({ kyc: uruMe.kyc, user: uruMe.user });
      } catch { /* non-critical */ }
      setPhase("success");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Verification failed. Please try again.";
      setKycError(msg);
      setPhase("collecting");
    }
  }

  function resetFlow() {
    setIsAdding(false);
    setPhase("selecting");
    setKycError(null);
    setPendingUuid(null);
    setResumeUrl(null);
    setFormData({
      email: prefill?.email ?? "", ssn: "",
      firstName: prefill?.firstName ?? "", lastName: prefill?.lastName ?? "", dob: "",
      addressLine1: "", addressCity: "", addressState: "", addressPostal: "",
      consented: false,
    });
  }

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-2xl font-bold text-text-primary">
            My Trust Factors
          </h3>
          <p className="text-text-secondary">
            Manage your verified credentials and identity anchors.
          </p>
        </div>
        {hasAddable && !isAdding && phase !== "success" && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <Icon name="add_circle" className="text-base" />
            Add More Trust
          </button>
        )}
        {isAdding && phase === "selecting" && (
          <button
            onClick={resetFlow}
            className="flex shrink-0 items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-text-secondary transition-colors hover:bg-gray-50"
          >
            <Icon name="close" className="text-base" />
            Cancel
          </button>
        )}
      </div>

      {/* Factor cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {factors.map((factor) => {
          const badge = getFactorBadge(factor.status);
          const isAddable = addableFactor(factor);
          const showToggle = isAdding && isAddable && phase === "selecting";

          return (
            <div
              key={factor.label}
              className={cn(
                "flex items-center justify-between rounded-2xl border bg-white p-5 shadow-sm transition-colors",
                factor.fullWidth && "md:col-span-2",
                showToggle
                  ? "border-primary-200 bg-primary-50/30"
                  : "border-gray-200",
              )}
            >
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                    factor.iconBg,
                    factor.iconColor,
                  )}
                >
                  <Icon name={factor.icon} className="text-2xl" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-text-primary">{factor.label}</p>
                  <p className="truncate text-xs text-text-secondary">
                    {factor.detail}
                  </p>
                </div>
              </div>

              {showToggle ? (
                <AskToggle
                  enabled={toggles[factor.configKey!] ?? true}
                  onToggle={(v) => handleToggle(factor.configKey!, v)}
                />
              ) : (
                <span
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider",
                    badge.className,
                  )}
                >
                  {badge.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ---- Add More Trust flow ---- */}

      {/* Phase: selecting — show "Continue" button */}
      {isAdding && phase === "selecting" && (
        <div className="rounded-2xl border border-primary-100 bg-primary-50/40 p-5 space-y-4">
          <p className="text-sm text-text-secondary">
            Toggle the factors above you want to add, then continue.
          </p>
          {selectedKeys.length === 0 && (
            <p className="text-xs text-amber-700">
              Toggle at least one factor to Ask before continuing.
            </p>
          )}
          <button
            onClick={() => {
              if (needsDataInput) {
                setPhase("collecting");
              } else {
                setPhase("submitting");
                void handleSubmit();
              }
            }}
            disabled={selectedKeys.length === 0}
            className="flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
          >
            <Icon name="arrow_forward" className="text-base" />
            Continue
          </button>
        </div>
      )}

      {/* Phase: collecting — data input form */}
      {phase === "collecting" && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
          <h4 className="font-semibold text-text-primary">Enter Verification Details</h4>

          {toggles["email"] && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-text-secondary">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
              />
            </div>
          )}

          {toggles["ssn"] && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                    placeholder="First"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                    placeholder="Last"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-text-secondary">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData((p) => ({ ...p, dob: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-text-secondary">SSN (9 digits, no dashes)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={11}
                  value={formData.ssn}
                  onChange={(e) => setFormData((p) => ({ ...p, ssn: e.target.value }))}
                  placeholder="123-45-6789"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                />
              </div>
            </>
          )}

          {toggles["address"] && (
            <div className="space-y-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-text-secondary">Street Address</label>
                <input
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData((p) => ({ ...p, addressLine1: e.target.value }))}
                  placeholder="123 Main St"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1 space-y-1">
                  <label className="text-xs font-semibold text-text-secondary">City</label>
                  <input
                    type="text"
                    value={formData.addressCity}
                    onChange={(e) => setFormData((p) => ({ ...p, addressCity: e.target.value }))}
                    placeholder="City"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary">State</label>
                  <input
                    type="text"
                    maxLength={2}
                    value={formData.addressState}
                    onChange={(e) => setFormData((p) => ({ ...p, addressState: e.target.value.toUpperCase() }))}
                    placeholder="CA"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary">ZIP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    value={formData.addressPostal}
                    onChange={(e) => setFormData((p) => ({ ...p, addressPostal: e.target.value }))}
                    placeholder="94102"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Consent */}
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={formData.consented}
              onChange={(e) => setFormData((p) => ({ ...p, consented: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded accent-primary-500"
            />
            <span className="text-xs text-text-secondary">
              I consent to the verification of the above information for identity purposes.
            </span>
          </label>

          {hasInteractive && (
            <p className="text-xs text-text-secondary">
              <Icon name="info" className="inline text-sm text-primary-500" />{" "}
              Gov ID / Liveness scan will open in your browser after you submit.
            </p>
          )}

          {kycError && <p className="text-xs text-error">{kycError}</p>}

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={!formData.consented}
              className="flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
            >
              Start Verification
            </button>
            <button
              onClick={() => setPhase("selecting")}
              className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:bg-gray-50"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Phase: submitting */}
      {phase === "submitting" && (
        <div className="flex items-center gap-3 rounded-2xl border border-primary-100 bg-primary-50/40 p-5">
          <Icon name="progress_activity" className="text-2xl text-primary-500 animate-spin" />
          <p className="text-sm font-semibold text-text-primary">Starting verification…</p>
        </div>
      )}

      {/* Phase: existing pending request */}
      {phase === "existing" && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="info" className="mt-0.5 shrink-0 text-xl text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                You have a pending verification
              </p>
              <p className="mt-1 text-xs text-amber-800">
                A verification request is already in progress. Resume it or wait for it to complete.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            {resumeUrl && (
              <a
                href={resumeUrl}
                className="flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
              >
                <Icon name="open_in_new" className="text-base" />
                Resume Verification
              </a>
            )}
            <button
              onClick={resetFlow}
              className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:bg-gray-50"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Phase: success */}
      {phase === "success" && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 space-y-3">
          <div className="flex items-center gap-3">
            <Icon name="check_circle" filled className="text-2xl text-emerald-600" />
            <p className="text-sm font-semibold text-emerald-900">
              Verification complete! Your trust factors have been updated.
            </p>
          </div>
          <button
            onClick={resetFlow}
            className="rounded-xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
          >
            Done
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Privacy & Security Section
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// PasskeyRow — single passkey with inline rename + delete
// ---------------------------------------------------------------------------

interface PasskeyRowProps {
  passkey: PasskeyInfo;
  onRenamed: (id: number, newName: string) => void;
  onDeleted: (id: number) => void;
}

function PasskeyRow({ passkey, onRenamed, onDeleted }: PasskeyRowProps) {
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(passkey.friendly_name);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function handleRename() {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === passkey.friendly_name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updatePasskey(passkey.id, { friendly_name: trimmed });
      onRenamed(passkey.id, trimmed);
      setEditing(false);
    } catch {
      // Revert on failure
      setNameValue(passkey.friendly_name);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deletePasskey(passkey.id);
      onDeleted(passkey.id);
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
      <Icon name="passkey" className="shrink-0 text-xl text-primary-500" />
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
                if (e.key === "Escape") {
                  setEditing(false);
                  setNameValue(passkey.friendly_name);
                }
              }}
              autoFocus
              className="flex-1 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
            />
            <button
              onClick={handleRename}
              disabled={saving}
              className="text-sm font-bold text-primary-500 hover:text-primary-600"
            >
              {saving ? "…" : "Save"}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setNameValue(passkey.friendly_name);
              }}
              className="text-sm text-text-muted hover:text-text-secondary"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-text-primary">
              {passkey.friendly_name}
            </span>
            <button
              onClick={() => setEditing(true)}
              className="shrink-0 text-xs text-text-muted hover:text-primary-500"
              aria-label="Rename passkey"
            >
              <Icon name="edit" className="text-sm" />
            </button>
          </div>
        )}
      </div>
      {confirmDelete ? (
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-red-600">Remove?</span>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-xs font-bold text-red-600 hover:text-red-700"
          >
            {deleting ? "…" : "Yes"}
          </button>
          <button
            onClick={() => setConfirmDelete(false)}
            className="text-xs text-text-muted hover:text-text-secondary"
          >
            No
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirmDelete(true)}
          className="shrink-0 text-text-muted transition-colors hover:text-error"
          aria-label="Delete passkey"
        >
          <Icon name="delete" className="text-lg" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Privacy & Security Section
// ---------------------------------------------------------------------------

interface PrivacySectionProps {
  initialPasskeys: PasskeyInfo[];
  initialSearchable: boolean;
  initialPublicProfile: boolean;
  connectionCode: string | null;
}

function PrivacySection({ initialPasskeys, initialSearchable, initialPublicProfile, connectionCode }: PrivacySectionProps) {
  const [showPasskeys, setShowPasskeys] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeyInfo[]>(initialPasskeys);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [searchable, setSearchable] = useState(initialSearchable);
  const [searchableSaving, setSearchableSaving] = useState(false);
  const [searchableError, setSearchableError] = useState<string | null>(null);
  const [publicProfile, setPublicProfile] = useState(initialPublicProfile);
  const [publicProfileSaving, setPublicProfileSaving] = useState(false);
  const [publicProfileError, setPublicProfileError] = useState<string | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const webAuthnSupported = browserSupportsWebAuthn();

  // Keep in sync if parent reloads the list
  useEffect(() => {
    setPasskeys(initialPasskeys);
  }, [initialPasskeys]);

  function handleRenamed(id: number, newName: string) {
    setPasskeys((prev) =>
      prev.map((p) => (p.id === id ? { ...p, friendly_name: newName } : p)),
    );
  }

  function handleDeleted(id: number) {
    setPasskeys((prev) => prev.filter((p) => p.id !== id));
  }

  async function handleAddPasskey() {
    if (!webAuthnSupported) return;
    setAdding(true);
    setAddError(null);
    try {
      const beginData = await passkeyRegisterBegin();
      let regResponse: RegistrationResponseJSON;
      try {
        regResponse = await startRegistration({
          optionsJSON: beginData.publicKey,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "";
        if (
          msg.toLowerCase().includes("cancel") ||
          msg.toLowerCase().includes("abort")
        ) {
          setAdding(false);
          return;
        }
        throw err;
      }
      const newPasskey = await passkeyRegisterComplete({
        challenge_id: beginData.challenge_id,
        friendly_name: "New Passkey",
        credential: {
          id: regResponse.id,
          rawId: regResponse.rawId,
          type: regResponse.type,
          response: {
            clientDataJSON: regResponse.response.clientDataJSON,
            attestationObject: regResponse.response.attestationObject,
          },
          transports: regResponse.response.transports as
            | AuthenticatorTransport[]
            | undefined,
        },
      });
      setPasskeys((prev) => [...prev, newPasskey]);
    } catch {
      setAddError("Failed to add passkey — please try again.");
    } finally {
      setAdding(false);
    }
  }

  const passkeyCount = passkeys.length;

  async function handleSearchableToggle() {
    const next = !searchable;
    setSearchable(next);
    setSearchableSaving(true);
    setSearchableError(null);
    try {
      await saveGlobalLinkConfig({ reality_check_searchable: next });
    } catch {
      // Revert optimistic update on failure
      setSearchable(!next);
      setSearchableError("Failed to save — please try again.");
    } finally {
      setSearchableSaving(false);
    }
  }

  async function handlePublicProfileToggle() {
    const next = !publicProfile;
    setPublicProfile(next);
    setPublicProfileSaving(true);
    setPublicProfileError(null);
    try {
      await saveGlobalLinkConfig({ public_profile_enabled: next });
    } catch {
      setPublicProfile(!next);
      setPublicProfileError("Failed to save — please try again.");
    } finally {
      setPublicProfileSaving(false);
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-2xl font-bold text-text-primary">
          Privacy & Security
        </h3>
        <p className="text-text-secondary">
          Control how your data is accessed and secured.
        </p>
      </div>

      <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white">

        {/* Reality Check Visibility */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-4">
              <Icon
                name="manage_search"
                className="shrink-0 text-2xl text-primary-500"
              />
              <div>
                <p className="font-bold text-text-primary">
                  Reality Check Visibility
                </p>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Allow people to find your verified status by searching your
                  phone number, email, or name on the{" "}
                  <span className="font-semibold">Reality Check</span> page.
                  Turn off to hide from all searches.
                </p>
                {searchableError && (
                  <p className="mt-1.5 text-xs text-red-600">{searchableError}</p>
                )}
              </div>
            </div>

            {/* Toggle */}
            <button
              role="switch"
              aria-checked={searchable}
              aria-label="Appear in Reality Check searches"
              disabled={searchableSaving}
              onClick={handleSearchableToggle}
              className={cn(
                "relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 disabled:opacity-50",
                searchable ? "bg-primary-500" : "bg-gray-300",
              )}
            >
              <span
                className={cn(
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200",
                  searchable ? "translate-x-5" : "translate-x-0",
                )}
              />
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
                searchable
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-text-muted",
              )}
            >
              <Icon
                name={searchable ? "visibility" : "visibility_off"}
                className="text-sm"
              />
              {searchable ? "Visible in searches" : "Hidden from searches"}
            </span>
            {searchableSaving && (
              <span className="text-xs text-text-muted">Saving…</span>
            )}
          </div>
        </div>

        {/* Public Profile */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-4">
              <Icon
                name="account_circle"
                className="shrink-0 text-2xl text-primary-500"
              />
              <div>
                <p className="font-bold text-text-primary">
                  Public Verified Profile
                </p>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Share a public link showing your verified badges — no personal
                  data, just proof that you're a real, verified human.
                </p>
                {publicProfileError && (
                  <p className="mt-1.5 text-xs text-red-600">{publicProfileError}</p>
                )}
              </div>
            </div>

            {/* Toggle */}
            <button
              role="switch"
              aria-checked={publicProfile}
              aria-label="Enable public verified profile"
              disabled={publicProfileSaving}
              onClick={handlePublicProfileToggle}
              className={cn(
                "relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 disabled:opacity-50",
                publicProfile ? "bg-primary-500" : "bg-gray-300",
              )}
            >
              <span
                className={cn(
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200",
                  publicProfile ? "translate-x-5" : "translate-x-0",
                )}
              />
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
                publicProfile
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-text-muted",
              )}
            >
              <Icon
                name={publicProfile ? "public" : "public_off"}
                className="text-sm"
              />
              {publicProfile ? "Profile is public" : "Profile is private"}
            </span>
            {publicProfileSaving && (
              <span className="text-xs text-text-muted">Saving…</span>
            )}
          </div>

          {/* URL row — only shown when enabled and connection code is available */}
          {publicProfile && connectionCode && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
              <Icon name="link" className="shrink-0 text-base text-text-muted" />
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-text-secondary">
                {`${window.location.origin}/p/${connectionCode}`}
              </span>
              <button
                onClick={() => {
                  void navigator.clipboard
                    .writeText(`${window.location.origin}/p/${connectionCode}`)
                    .then(() => {
                      setUrlCopied(true);
                      setTimeout(() => setUrlCopied(false), 2000);
                    });
                }}
                className="shrink-0 text-xs font-bold text-primary-500 transition-colors hover:text-primary-600"
                aria-label="Copy profile URL"
              >
                {urlCopied ? "Copied!" : "Copy"}
              </button>
            </div>
          )}
        </div>

        {/* Passkey Management */}
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              <Icon
                name="passkey"
                className="shrink-0 text-2xl text-primary-500"
              />
              <div>
                <p className="font-bold text-text-primary">
                  Passkey Management
                </p>
                <p className="text-sm text-text-secondary">
                  Use biometrics instead of passwords. {passkeyCount} passkey
                  {passkeyCount !== 1 ? "s" : ""} active.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPasskeys((prev) => !prev)}
              className="shrink-0 text-sm font-bold text-primary-500 transition-colors hover:text-primary-600 hover:underline"
            >
              {showPasskeys ? "Done" : "Manage"}
            </button>
          </div>

          {/* Expandable passkey list */}
          {showPasskeys && (
            <div className="mt-4 space-y-2">
              {passkeys.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-sm text-text-muted">
                  No passkeys registered yet.
                </p>
              ) : (
                passkeys.map((pk) => (
                  <PasskeyRow
                    key={pk.id}
                    passkey={pk}
                    onRenamed={handleRenamed}
                    onDeleted={handleDeleted}
                  />
                ))
              )}
              {addError && <p className="text-sm text-error">{addError}</p>}
              <button
                onClick={handleAddPasskey}
                disabled={adding || !webAuthnSupported}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary-300 py-3 text-sm font-bold text-primary-500 transition-colors hover:border-primary-500 hover:bg-primary-50 disabled:opacity-50"
              >
                {adding ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-300 border-t-primary-500" />
                    Follow the browser prompt…
                  </>
                ) : (
                  <>
                    <Icon name="add" className="text-lg" />
                    {webAuthnSupported
                      ? "Add Another Passkey"
                      : "Passkeys not supported on this device"}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Data Sharing Preferences */}
        <div className="flex items-center justify-between p-5 opacity-50 sm:p-6">
          <div className="flex gap-4">
            <Icon
              name="share_reviews"
              className="shrink-0 text-2xl text-text-muted"
            />
            <div>
              <p className="font-bold text-text-primary">
                Data Sharing Preferences
              </p>
              <p className="text-sm text-text-secondary">
                Control which third-parties can request your zero-knowledge
                proofs.
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-text-muted">
            Coming Soon
          </span>
        </div>

        {/* Zero-Knowledge Backups */}
        <div className="flex items-center justify-between p-5 opacity-50 sm:p-6">
          <div className="flex gap-4">
            <Icon
              name="cloud_sync"
              className="shrink-0 text-2xl text-text-muted"
            />
            <div>
              <p className="font-bold text-text-primary">
                Zero-Knowledge Backups
              </p>
              <p className="text-sm text-text-secondary">
                Encrypted backup of your identity factors on decentralized
                storage.
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-text-muted">
            Coming Soon
          </span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Account Details Section (Profile + Delete)
// ---------------------------------------------------------------------------

interface AccountSectionProps {
  userId: number;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  onLogout: () => void;
  onProfileUpdated: (
    firstName: string,
    lastName: string,
    email: string,
  ) => void;
  onAvatarUpdated: (newUrl: string) => void;
}

function AccountSection({
  userId,
  fullName,
  firstName,
  lastName,
  email,
  avatarUrl,
  onLogout,
  onProfileUpdated,
  onAvatarUpdated,
}: AccountSectionProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFirst, setEditFirst] = useState(firstName);
  const [editLast, setEditLast] = useState(lastName);
  const [editEmail, setEditEmail] = useState(email);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleAvatarFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const avatarUrl = await uploadAvatar(userId, file);
      onAvatarUpdated(avatarUrl);
    } catch {
      setUploadError("Upload failed — please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function startEdit() {
    setEditFirst(firstName);
    setEditLast(lastName);
    setEditEmail(email);
    setSaveError(null);
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      await updateProfile(userId, {
        first_name: editFirst.trim(),
        last_name: editLast.trim(),
        email: editEmail.trim() || undefined,
      });
      onProfileUpdated(editFirst.trim(), editLast.trim(), editEmail.trim());
      setEditing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setSaveError(msg ? msg : "Failed to save — please try again.");
    } finally {
      setSaving(false);
    }
  }

  const initials = fullName
    .split(" ")
    .map((p) => p[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-2xl font-bold text-text-primary">
          Account Details
        </h3>
        <p className="text-text-secondary">Personal information.</p>
      </div>

      {/* Profile Card */}
      <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6">
        <h4 className="flex items-center gap-2 font-bold text-text-primary">
          <Icon name="info" className="text-sm" />
          Profile
        </h4>

        {/* Avatar + name summary */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="group relative h-16 w-16 flex-shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Change profile photo"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={fullName}
                className="h-16 w-16 rounded-full object-cover ring-2 ring-primary-200"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 ring-2 ring-primary-200">
                <span className="text-xl font-black text-primary-500">
                  {initials}
                </span>
              </div>
            )}
            {/* Camera overlay on hover */}
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 group-disabled:opacity-0">
              {uploading ? (
                <Icon name="progress_activity" className="animate-spin text-white" />
              ) : (
                <Icon name="photo_camera" className="text-white text-sm" />
              )}
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarFileChange}
          />
          <div>
            <p className="font-bold text-text-primary">{fullName}</p>
            <p className="text-sm text-text-secondary">
              {email || "No email provided"}
            </p>
            {uploadError && (
              <p className="mt-1 text-xs text-red-600">{uploadError}</p>
            )}
          </div>
        </div>

        {/* Fields — view or edit mode */}
        <div className="space-y-3 border-t border-gray-100 pt-3">
          {editing ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-text-muted">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editFirst}
                    onChange={(e) => setEditFirst(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-text-muted">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editLast}
                    onChange={(e) => setEditLast(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-text-muted">
                  Email Address
                </label>
                <input
                  type="email"
                  value={editing ? editEmail : (email || "")}
                  readOnly={!editing}
                  disabled={!editing}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="your@email.com"
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm transition-colors ${
                    editing
                      ? "border-gray-300 bg-white text-text-primary focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                      : "border-gray-200 bg-gray-50 text-text-muted cursor-not-allowed"
                  }`}
                />
              </div>
              {saveError && <p className="text-sm text-error">{saveError}</p>}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-text-secondary transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-text-muted">
                  Full Name
                </label>
                <p className="font-medium text-text-primary">{fullName}</p>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-text-muted">
                  Primary Email
                </label>
                <p className="font-medium text-text-primary">
                  {email || "Not provided"}
                </p>
              </div>
              <button
                onClick={startEdit}
                className="pt-2 text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
              >
                Edit Details
              </button>
            </>
          )}
        </div>
      </div>

      {/* Danger Zone — Delete Account */}
      <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-red-100 bg-red-50 p-6 sm:flex-row sm:items-center">
        <div>
          <p className="font-bold text-red-700">Delete Account</p>
          <p className="text-sm text-red-600/70">
            Permanently remove your identity data and vault. This cannot be
            undone.
          </p>
        </div>
        {showDeleteConfirm ? (
          <div className="flex shrink-0 gap-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-text-secondary transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // TODO: Wire to delete account API endpoint once available
                console.warn(
                  "Delete account action triggered — no API endpoint available",
                );
                onLogout();
              }}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-red-700"
            >
              Confirm Delete
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="shrink-0 rounded-xl border border-red-200 px-4 py-2 text-sm font-bold text-red-600 transition-colors hover:bg-red-100"
          >
            Delete
          </button>
        )}
      </div>

      {/* Sign Out — visible on mobile (desktop has it in the sidebar) */}
      <div className="border-t border-gray-200 pt-4 md:hidden">
        <button
          onClick={onLogout}
          className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium text-error transition-colors hover:bg-red-50"
        >
          <Icon name="logout" className="text-lg" />
          Sign Out
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section Tabs (top, all sizes — replaces the old left sidebar)
// ---------------------------------------------------------------------------

interface MobileTabBarProps {
  activeSection: SectionId;
  onNavigate: (section: SectionId) => void;
}

function SectionTabs({ activeSection, onNavigate }: MobileTabBarProps) {
  return (
    <div className="inline-flex items-center gap-1 overflow-x-auto rounded-xl bg-white p-1 shadow-[0px_4px_20px_rgba(26,31,43,0.04)]">
      {NAV_ITEMS.map((item) => {
        const isActive = activeSection === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors",
              isActive ? "bg-[#fef0f0] text-[#974446]" : "text-[#877271] hover:text-[#161b27]",
            )}
          >
            <Icon name={item.icon} className="text-base" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Settings Page Component
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  const { user, logout } = useAuth();

  const [activeSection, setActiveSection] = useState<SectionId>("identity");
  const [passkeys, setPasskeys] = useState<PasskeyInfo[]>([]);
  const [kyc, setKyc] = useState<UruKyc | null>(null);
  const [uruMeUser, setUruMeUser] = useState<UruMeUser | null>(null);
  const [connectionConfig, setConnectionConfig] = useState<Record<string, boolean>>({});
  const [connectionCode, setConnectionCode] = useState<string | null>(null);

  // Fetch passkeys and uru profile (KYC data) in parallel
  useEffect(() => {
    let cancelled = false;

    listPasskeys().then(
      (list) => {
        if (!cancelled) setPasskeys(list);
      },
      () => {
        // Non-critical — passkey list will be empty
      },
    );

    getUruMe().then(
      (uruMe) => {
        if (!cancelled) {
          setKyc(uruMe.kyc);
          setUruMeUser(uruMe.user);
          setConnectionConfig(uruMe.connection_config);
          setConnectionCode(uruMe.connection_code);
        }
      },
      () => {
        // Non-critical — identity factors will show "not linked" for all flags
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const handleNavigate = useCallback((section: SectionId) => {
    setActiveSection(section);
  }, []);

  // Derived user data — prefer getUruMe() user which is sourced from KYC records;
  // fall back to the auth user profile if uru/me hasn't loaded yet.
  const [editableFirstName, setEditableFirstName] = useState("");
  const [editableLastName, setEditableLastName] = useState("");
  const [editableEmail, setEditableEmail] = useState("");

  // Sync editable fields when uru/me or auth user loads
  useEffect(() => {
    const fn = uruMeUser?.first_name || user?.first_name || "";
    const ln = uruMeUser?.last_name || user?.last_name || "";
    const rawEmail = uruMeUser?.email || user?.email || "";
    const em = rawEmail.endsWith("@uru.youareyou.io") ? "" : rawEmail;
    setEditableFirstName(fn);
    setEditableLastName(ln);
    setEditableEmail(em);
  }, [uruMeUser, user]);

  const fullName = `${editableFirstName} ${editableLastName}`.trim() || "User";
  const avatarUrl = resolveAvatarUrl(uruMeUser?.avatar ?? user?.avatar ?? null);
  const email = editableEmail;
  const phone = user?.phone_number ?? user?.username;

  const handleProfileUpdated = useCallback(
    (firstName: string, lastName: string, updatedEmail: string) => {
      setEditableFirstName(firstName);
      setEditableLastName(lastName);
      setEditableEmail(updatedEmail);
    },
    [],
  );

  const handleAvatarUpdated = useCallback((newUrl: string) => {
    setUruMeUser((prev) => prev ? { ...prev, avatar: newUrl } : prev);
  }, []);

  const factors = buildIdentityFactors(
    phone,
    email,
    kyc,
    user?.is_phone_verified ?? false,
  );

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 md:py-12">
      {/* Top tabs + Sign Out (replaces the old left sidebar) */}
      <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
        <SectionTabs activeSection={activeSection} onNavigate={handleNavigate} />
        <button
          onClick={logout}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[#ba1a1a] transition-colors hover:bg-[#ffe6e3]"
        >
          <Icon name="logout" className="text-base" />
          Sign Out
        </button>
      </div>

      {/* Active section */}
      <div>
        <div>
          {activeSection === "identity" && (
            <IdentitySection
              factors={factors}
              onKycUpdated={({ kyc: updatedKyc, user: updatedUser }) => {
                setKyc(updatedKyc);
                setUruMeUser(updatedUser);
              }}
              prefill={{
                email: editableEmail || undefined,
                firstName: editableFirstName || undefined,
                lastName: editableLastName || undefined,
              }}
            />
          )}
          {activeSection === "privacy" && (
            <PrivacySection
              initialPasskeys={passkeys}
              initialSearchable={uruMeUser?.reality_check_searchable ?? true}
              initialPublicProfile={connectionConfig.public_profile_enabled ?? false}
              connectionCode={connectionCode}
            />
          )}
          {activeSection === "account" && (
            <AccountSection
              userId={user?.id ?? 0}
              fullName={fullName}
              firstName={editableFirstName}
              lastName={editableLastName}
              email={email}
              avatarUrl={avatarUrl}
              onLogout={logout}
              onProfileUpdated={handleProfileUpdated}
              onAvatarUpdated={handleAvatarUpdated}
            />
          )}

          {/* Footer */}
          <footer className="mt-12 border-t border-gray-200 py-8 text-center">
            <p className="text-sm text-text-muted">
              &copy; {new Date().getFullYear()} <BrandName /> Inc. All identity
              factors are cryptographically secured.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
